import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { AuthSessionFacade } from '@services/auth-session/auth-session.facade';
import { firstValueFrom } from 'rxjs';

import {
  CLEAR_AUTH_SESSION,
  GET_USER,
  GET_USER_MOCK,
  POPULATE_POLICY_MANAGEMENT,
  STORE_QUERY_PARAMS,
} from 'src/app/store/user/user.action';
import { GET_NOTIFICATION_LIST } from 'src/app/store/notification/notification.action';

@Injectable({
  providedIn: 'root'
})
export class LoginResolverService implements Resolve<any> {

  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly authSessionFacade: AuthSessionFacade,
  ) { }

  async resolve(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Promise<any> {
    const token = route.queryParamMap.get('token');
    const queryParams = this.extractQueryParams(route);

    if (!token) {
      return this.resolveWithoutToken(queryParams);
    }

    return this.resolveWithToken(token, queryParams);
  }

  private async resolveWithoutToken(queryParams: any): Promise<any> {
    let userInfo = this.store.selectSnapshot<any>((state) => state.UserState.user);

    if (!userInfo?.userId) {
      return this.redirectToNotFound();
    }

    await firstValueFrom(this.store.dispatch(new STORE_QUERY_PARAMS(queryParams)));
    this.authSessionFacade.setSession(userInfo, queryParams);
    if (!this.authSessionFacade.hasSession()) {
      return this.redirectToNotFound();
    }
    await this.handleUserResponse();

    return userInfo;
  }

  private async resolveWithToken(token: string, queryParams: any): Promise<any> {
    const payload = this.createPayload(token, queryParams);

    await firstValueFrom(
      queryParams.mock
        ? this.store.dispatch(new GET_USER_MOCK(payload))
        : this.store.dispatch(new GET_USER(payload)),
    );

    const userInfo = this.store.selectSnapshot<any>((state) => state.UserState.user);
    if (!userInfo?.userId) {
      return this.redirectToNotFound();
    }

    this.authSessionFacade.setSession(userInfo, queryParams);
    await this.handleUserResponse();
    return userInfo;
  }

  private redirectToNotFound(): null {
    this.store.dispatch(new CLEAR_AUTH_SESSION());
    this.router.navigate(['/404']);
    return null;
  }

  private extractQueryParams(route: ActivatedRouteSnapshot): any {
    const queryParams = {
      mock: route.queryParamMap.get('mock'),
      renewNow: route.queryParamMap.get('renewNow'),
      agentCode: route.queryParamMap.get('agentCode'),
      policyNo: route.queryParamMap.get('policyNo'),
      claimNo: route.queryParamMap.get('claimNo'),
      action: route.queryParamMap.get('action'),
      contractId: route.queryParamMap.get('contractId'),
      documentNo: route.queryParamMap.get('documentNo'),
      productType: route.queryParamMap.get('productType'),
      cnoteType: route.queryParamMap.get('cnoteType') === 'NW' ? 'NWOO/RN' : route.queryParamMap.get('cnoteType'),
      draftStep: route.queryParamMap.get('draftStep'),
      idType: route.queryParamMap.get('idType'),
      idValue: route.queryParamMap.get('idValue'),
      clientType: route.queryParamMap.get('clientType'),
      enquiryProduct: route.queryParamMap.get('enquiryProduct'),
      searchCriteria: route.queryParamMap.get('searchCriteria'),
      searchValue: route.queryParamMap.get('searchValue'),
      opportunityId: route.queryParamMap.get('opportunityId'),
      vehNo: route.queryParamMap.get('vehNo'),
      origin: route.queryParamMap.get('origin'),
      productCode: route.queryParamMap.get('productCode'),
      criteria: route.queryParamMap.get('criteria'),
      criteriaValue: route.queryParamMap.get('criteriaValue'),
      status: route.queryParamMap.get('status'),
      startDate: route.queryParamMap.get('startDate'),
      endDate: route.queryParamMap.get('endDate'),
      branchCode: route.queryParamMap.get('branchCode'),
      referredItem: route.queryParamMap.get('referredItem'),
      coverNoteNo: route.queryParamMap.get('coverNoteNo'),
      vehicleNo: route.queryParamMap.get('vehicleNo'),
      marketerManager: route.queryParamMap.get('marketerManager'),
      isRTP: route.queryParamMap.get('isRTP'),
      isFromMotorApproval: route.queryParamMap.get('isFromMotorApproval'),
      ccManualClickSatMenu: route.queryParamMap.get('ccManualClickSatMenu')
    };
    return queryParams;
  }

  private createPayload(token: string, queryParams: any): any {
    return {
      userId: token,
      queryParams: queryParams
    };
  }

  private async handleUserResponse(): Promise<void> {
    const userId = this.store.selectSnapshot<string>(state => state.UserState.user.userId);
    if (!userId) {
      return;
    }

    const notificationData = { userId: userId, page: 0, size: 10 };
    await firstValueFrom(this.store.dispatch(new GET_NOTIFICATION_LIST(notificationData, false)));
    await firstValueFrom(this.store.dispatch(new POPULATE_POLICY_MANAGEMENT()));
  }
}